@file:Suppress("TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.pages.promocodes

import com.nonopn.skydome.common.app.api.impl.AdminPromotionCode
import com.nonopn.skydome.common.app.api.impl.ApiAdmin
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skywatcher.app.utils.safeLaunch
import eu.codlab.viewmodel.StateViewModel
import korlibs.time.DateTime
import korlibs.time.DateTimeTz
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

internal fun promoNowSeconds(): Long = DateTime.now().unixMillisLong / 1000L

/** Offset of the device from UTC, in seconds (end dates are typed as local days). */
internal fun promoLocalOffsetSeconds(): Long = (DateTimeTz.nowLocal().offset.totalMilliseconds / 1000).toLong()

/** Codes of the last GET, shared by the list and the form (the form of a code opens from the list). */
object PromoCodesAdminStore {
    private val mutableCodes = MutableStateFlow<List<AdminPromotionCode>>(emptyList())
    val codes: StateFlow<List<AdminPromotionCode>> = mutableCodes

    fun find(uuid: String): AdminPromotionCode? = mutableCodes.value.firstOrNull { it.uuid == uuid }

    fun replaceAll(codes: List<AdminPromotionCode>) {
        mutableCodes.value = codes
    }

    /** Saved code (answer of the POST): replaces the code with the same uuid, or is added first. */
    fun upsert(code: AdminPromotionCode) {
        val current = mutableCodes.value
        mutableCodes.value = if (current.any { it.uuid == code.uuid }) {
            current.map { if (it.uuid == code.uuid) code else it }
        } else {
            listOf(code) + current
        }
    }

    fun remove(uuid: String) {
        mutableCodes.value = mutableCodes.value.filterNot { it.uuid == uuid }
    }
}

/** App filter of the list (codes of every app are listed by the admin route). */
enum class PromoAppFilter(val app: String?) { All(null), StrikeRadar("StrikeRadar"), SkySentinel("SkySentinel") }

/** Message after disable / enable (with "Undo") or after a deletion (no undo). */
data class PromoNotice(val code: AdminPromotionCode, val kind: Kind) {
    enum class Kind { Disabled, Enabled, Deleted }
}

data class PromoCodesAdminState(
    val loading: Boolean = true,
    val loadFailed: Boolean = false,
    val query: String = "",
    val stateFilter: PromoStateFilter = PromoStateFilter.All,
    val appFilter: PromoAppFilter = PromoAppFilter.All,
    val busyUuid: String? = null,
    val pendingDelete: AdminPromotionCode? = null,
    val notice: PromoNotice? = null,
    val actionFailed: Boolean = false,
)

/** Administrator list of the promotion codes: load, search, disable / enable / delete. */
class PromoCodesAdminModel(
    private val appModel: AppModel,
) : StateViewModel<PromoCodesAdminState>(PromoCodesAdminState()) {

    init {
        load()
    }

    private fun api(): ApiAdmin? = appModel.states.value.apiProvider?.apiAdmin as? ApiAdmin

    fun load() {
        val api = api()
        if (api == null) {
            updateState { copy(loading = false, loadFailed = true) }
            return
        }
        updateState { copy(loading = true, loadFailed = false) }
        safeLaunch {
            try {
                PromoCodesAdminStore.replaceAll(api.promotionCodes())
                updateState { copy(loading = false) }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Throwable) {
                updateState { copy(loading = false, loadFailed = true) }
            }
        }
    }

    fun setQuery(query: String) = updateState { copy(query = query) }

    fun setStateFilter(filter: PromoStateFilter) = updateState { copy(stateFilter = filter) }

    fun setAppFilter(filter: PromoAppFilter) = updateState { copy(appFilter = filter) }

    /** Disable (reversible) or enable: POST of the COMPLETE code read from the GET with only `active` changed. */
    fun setActive(code: AdminPromotionCode, active: Boolean, fromUndo: Boolean = false) {
        val api = api() ?: return
        if (states.value.busyUuid != null) return
        // Referrer without email: the POST would erase the referral
        if (code.referrerLocked()) return
        updateState { copy(busyUuid = code.uuid, actionFailed = false, notice = null, pendingDelete = null) }
        safeLaunch {
            try {
                val saved = api.savePromotionCode(code.toRequest(active = active))
                PromoCodesAdminStore.upsert(saved)
                val kind = if (active) PromoNotice.Kind.Enabled else PromoNotice.Kind.Disabled
                updateState { copy(busyUuid = null, notice = if (fromUndo) null else PromoNotice(saved, kind)) }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Throwable) {
                updateState { copy(busyUuid = null, actionFailed = true) }
            }
        }
    }

    /** "Undo" of the last disable / enable. */
    fun undo() {
        val notice = states.value.notice ?: return
        val current = PromoCodesAdminStore.find(notice.code.uuid) ?: return
        when (notice.kind) {
            PromoNotice.Kind.Disabled -> setActive(current, true, fromUndo = true)
            PromoNotice.Kind.Enabled -> setActive(current, false, fromUndo = true)
            PromoNotice.Kind.Deleted -> Unit
        }
    }

    fun dismissNotice() = updateState { copy(notice = null, actionFailed = false) }

    fun requestDelete(code: AdminPromotionCode) = updateState { copy(pendingDelete = code) }

    fun cancelDelete() = updateState { copy(pendingDelete = null) }

    /** "Disable instead" of the deletion dialog of a used code. */
    fun disableInstead() {
        val code = states.value.pendingDelete ?: return
        updateState { copy(pendingDelete = null) }
        setActive(code, false)
    }

    /** DELETE, irreversible: only after the explicit confirmation of the dialog. */
    fun confirmDelete() {
        val code = states.value.pendingDelete ?: return
        val api = api() ?: return
        updateState { copy(pendingDelete = null, busyUuid = code.uuid, actionFailed = false, notice = null) }
        safeLaunch {
            try {
                api.deletePromotionCode(code.uuid)
                PromoCodesAdminStore.remove(code.uuid)
                updateState { copy(busyUuid = null, notice = PromoNotice(code, PromoNotice.Kind.Deleted)) }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Throwable) {
                updateState { copy(busyUuid = null, actionFailed = true) }
            }
        }
    }
}
